import React, { useCallback, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import cm from 'classnames';
import dayjs from 'dayjs';
import { Spinner } from 'ksnm-common-ui/lib/Spinner';
import { Tree } from 'ksnm-common-ui/lib/Tree';
import { sortBy } from 'lodash-es';

import { usePermission } from '@src/utils/hooks/usePermission';
import { logsStore } from '@stores/logsStore';
import { formatDate } from '@utils';
import type { EtlLogDto, EtlTypeDto } from '@src/api';

import styles from '../Logs.module.scss';

type EtlLogTreeDto = EtlLogDto & { children?: EtlLogTreeDto[]; title?: JSX.Element | string; key: string };

interface IProcessTree {
  fullSize: boolean;
  isProcessSelected: boolean;
  selectedProcess: string[];
  setSelectedProcess: React.Dispatch<React.SetStateAction<string[]>>;
  etl: EtlTypeDto[];
}

export const ProcessTree = observer(
  ({ fullSize, isProcessSelected, selectedProcess, setSelectedProcess, etl }: IProcessTree) => {
    const { isImportStarted, activeEtlProcesses, setFilterValues, setExpandedKeys, expandedKeys } = logsStore;

    const { VIEW: CmCompanyReportView } = usePermission('CmCompanyReport');
    const { VIEW: CmCtrlResultsView } = usePermission('CmCtrlResults');

    useEffect(() => {
      const openGroups = activeEtlProcesses
        .map((proc) => etl.find((type) => type.id === proc.etlTypeId)?.etlGroup)
        .filter((gr) => gr !== undefined);

      if (openGroups.length) setExpandedKeys([...expandedKeys, ...(openGroups as string[])]);
    }, [activeEtlProcesses, etl, expandedKeys, setExpandedKeys]);

    const tree = useMemo(() => {
      const filteredEtlTypes = etl.filter((type) =>
        type.etlGroup === 'Контроли' ? CmCtrlResultsView : CmCompanyReportView,
      );

      return sortBy(
        filteredEtlTypes.reduce((acc, type) => {
          const idx = acc.findIndex((tp) => tp.key === type?.etlGroup);

          if (idx > -1) {
            acc[idx].children?.push({
              key: type.processName ?? '',
              title: (
                <div className={styles.processName}>
                  <div>{type.processName}</div>
                  {isImportStarted && activeEtlProcesses.some((proc) => proc.etlTypeId === type.id) && (
                    <div className={styles.spin}>
                      <Spinner />
                    </div>
                  )}
                </div>
              ),
            });
          } else {
            acc.push({
              key: type.etlGroup ?? '',
              title: type.etlGroup,
              children: [
                {
                  title: (
                    <div className={styles.processName}>
                      <div>{type.processName}</div>
                      {isImportStarted && activeEtlProcesses.some((proc) => proc.etlTypeId === type.id) && (
                        <div className={styles.spin}>
                          <Spinner />
                        </div>
                      )}
                    </div>
                  ),
                  key: type.processName ?? `${type.id}`,
                },
              ],
            });
          }

          return acc;
        }, [] as EtlLogTreeDto[]),
        'key',
      );
    }, [CmCompanyReportView, CmCtrlResultsView, activeEtlProcesses, etl, isImportStarted]);

    const onSelect = useCallback(
      (selected) => {
        setFilterValues({
          dateFrom: formatDate({ date: dayjs().subtract(7, 'day'), outputFormat: 'YYYY-MM-DD' }),
          dateTo: formatDate({ date: new Date(), outputFormat: 'YYYY-MM-DD' }),
          guid: '',
        });
        setSelectedProcess([...(selected as string[])]);
      },
      [setFilterValues, setSelectedProcess],
    );

    const onExpand = useCallback(
      (expKeys) => {
        setExpandedKeys(expKeys);
      },
      [setExpandedKeys],
    );

    if (fullSize || !tree.length) return null;

    return (
      <Tree
        selectable
        showExpandButton={false}
        treeData={tree}
        onSelect={onSelect}
        expandedKeys={expandedKeys}
        onExpand={onExpand}
        selectedKeys={selectedProcess}
        className={cm(styles.tree, {
          [styles.withTable]: !!selectedProcess.length && isProcessSelected,
        })}
      />
    );
  },
);
